import { useEffect, useState } from "react";
import type { MailAlert, MailAlertSeverity, MailDashboard } from "@feedback-hub/contract/browser";

import { ApiError, getMailDashboard } from "../../api.js";
import { MailChart } from "./MailChartSvg.js";
import { dayTickLabel } from "./mailChart.js";
import { count, pct } from "./mailFormat.js";

const SEVERITY_LABELS: Record<MailAlertSeverity, string> = {
  critical: "Critical",
  warning: "Warning",
  info: "Info",
};

function AlertItem({ alert }: { alert: MailAlert }) {
  return (
    <li className={`mail-alert mail-alert-${alert.severity}`}>
      <span className="mail-alert-sev">{SEVERITY_LABELS[alert.severity]}</span>
      <div>
        <div className="mail-alert-title">{alert.title}</div>
        <div className="mail-alert-detail">{alert.detail}</div>
      </div>
    </li>
  );
}

function Tile({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: string;
  sub?: string | null;
  warn?: boolean;
}) {
  return (
    <div className="metric-card">
      <div className="label">{label}</div>
      <div className="value">
        {value}
        {warn && <span className="warn-mark">check</span>}
      </div>
      {sub && <div className="muted tile-sub">{sub}</div>}
    </div>
  );
}

export function DashboardTab() {
  const [data, setData] = useState<MailDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await getMailDashboard();
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load the mail dashboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <p className="muted">Loading mail dashboard…</p>;
  if (error || !data) return <p className="error">{error ?? "Failed to load the mail dashboard."}</p>;

  const { alerts, totals, reputation, daily, windowDays } = data;
  const unattributedTotal = reputation ? daily.reduce((s, p) => s + (p.unattributed ?? 0), 0) : null;
  const complaintSub = reputation
    ? [
        reputation.rateDay ? `as of ${dayTickLabel(reputation.rateDay)}` : null,
        reputation.lastComplaintDay ? `Last complaint ${dayTickLabel(reputation.lastComplaintDay)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="panel section">
      <h3>Alerts</h3>
      {alerts.length === 0 ? (
        <p className="muted">No alerts. Sending looks healthy.</p>
      ) : (
        <ul className="mail-alerts">
          {alerts.map((a) => (
            <AlertItem key={a.id} alert={a} />
          ))}
        </ul>
      )}

      <h3 className="dash-heading">Last {windowDays} days</h3>
      <div className="metrics-grid">
        <Tile label={`Sent (${windowDays} days)`} value={count(totals.sends)} />
        <Tile label="Delivered" value={count(totals.deliveries)} />
        <Tile label="Bounced" value={count(totals.bounces)} />
        <Tile label="Complained" value={count(totals.complaints)} />
        <Tile label="AWS complaint rate" value={pct(reputation?.complaintRate ?? null)} sub={complaintSub} />
        <Tile
          label="AWS bounce rate"
          value={pct(reputation?.bounceRate ?? null)}
          sub={reputation?.rateDay ? `as of ${dayTickLabel(reputation.rateDay)}` : null}
        />
        <Tile
          label={`Not recorded by gateway (${windowDays} days)`}
          value={unattributedTotal == null ? "—" : count(unattributedTotal)}
          warn={unattributedTotal != null && unattributedTotal > 0}
        />
      </div>

      <h3 className="dash-heading">Daily sends</h3>
      <MailChart points={daily} />
    </div>
  );
}
